/**
 * TGC section reveal: gives below-the-fold content sections the same soft
 * fade+rise the product cards already have, so scrolling down a page never
 * lands on something that just sits there statically.
 *
 * Design rules, learned the hard way in this theme (see the work log):
 *
 * 1. NOTHING here is hidden by static CSS. This script injects both the CSS
 *    and the hiding class itself, so if the script fails to load or throws,
 *    every section renders exactly as it does today. A static `opacity: 0`
 *    that only JS can undo once blanked the whole catalog.
 * 2. It reuses the one shared observer in @theme/scroll-reveal instead of
 *    creating its own - two uncoordinated observers animating the same
 *    elements is a documented past failure.
 * 3. Elements that are even partially inside the viewport at load are left
 *    completely alone. Only content the user has to scroll to animates.
 *    (Static sections are server-rendered, so the infinite-scroll edge case
 *    that forbids this check for product cards does not apply here.)
 * 4. The transform is removed after the animation finishes: a lingering
 *    transform on a section wrapper turns position:fixed children (sticky
 *    add-to-cart bar, drawers) into position:absolute ones.
 * 5. Only listen for the end of the REVEAL. The script runs after first paint,
 *    so adding the hiding class makes the element transition INTO its hidden
 *    state; listening from the start mistook that hide transition's end for the
 *    reveal's and cleaned up early - every section was silently un-hidden
 *    before anyone scrolled (2026-09-30, why the fade "stopped working").
 *
 * Also covered: the footer group (footer + utilities strip), and the bundle
 * widget piece by piece - its title, subtitle and each tile rise on their own,
 * like product cards, instead of one 1300px block where only the top moved.
 */

import { registerReveal } from '@theme/scroll-reveal';

const REVEAL_CLASS = 'tgc-section-reveal';

/** Sections that must never be animated as a whole. */
const EXCLUDE_INSIDE = [
  'results-list', // collection grid: its cards animate individually already
  'product-card', // any section of product cards: same reason - animating the section too doubles the motion
  '.floating-add-to-cart-section', // position:fixed child, see note 4
  'cart-drawer',
  '.shopify-product-form', // the buy box - interaction-heavy, and above the fold anyway
  '.bundle-widget--showcase', // its pieces animate individually, see BUNDLE_PARTS
].join(',');

/**
 * Recommendation/product-list sections render their cards asynchronously, so
 * the product-card exclusion above can't see them at init time. Exclude them
 * by section id instead - their cards animate individually once loaded.
 */
const EXCLUDE_ID = /product[_-]?(recommendations|list)/i;

const BUNDLE_PARTS = '.bundle-widget--showcase :is(.bundle-widget__title, .bundle-widget__subtitle, .bundle-card)';

/**
 * The utilities strip is ~43px and sits at the very end of the page, so it can
 * never cross the shared -50px trigger line; the footer group triggers at the edge.
 */
const FOOTER_OPTIONS = { rootMargin: '0px' };

/**
 * @param {HTMLElement} el
 * @param {object} [options] - passed through to registerReveal
 */
function arm(el, options) {
  el.classList.add(REVEAL_CLASS);

  // Strip everything once the reveal has played so no transform lingers
  // (rule 4). The classes' end state equals the natural state, so removing
  // them causes no visual jump. The timer covers transitionend not firing.
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timer;
  const cleanup = () => {
    clearTimeout(timer);
    el.removeEventListener('transitionend', onEnd);
    el.classList.remove(REVEAL_CLASS, 'is-revealed');
    el.style.removeProperty('--reveal-order');
  };
  /** @param {TransitionEvent} event */
  const onEnd = (event) => {
    if (event.target === el && (event.propertyName === 'transform' || event.propertyName === 'opacity')) cleanup();
  };
  // Arm the end listener and fallback timer only once the reveal starts (rule 5).
  const started = new MutationObserver(() => {
    if (!el.classList.contains('is-revealed')) return;
    started.disconnect();
    el.addEventListener('transitionend', onEnd);
    timer = setTimeout(cleanup, 2000);
  });
  started.observe(el, { attributes: true, attributeFilter: ['class'] });

  registerReveal(el, options);
}

/** @param {Element} el */
const belowFold = (el) => el.getBoundingClientRect().top >= window.innerHeight;

function init() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /** @type {[HTMLElement, object | undefined][]} */
  const candidates = [];
  const sections = [...document.querySelectorAll('#MainContent .shopify-section')];
  // Owner rule (2026-09-30): on pages with a product grid, nothing above the
  // first grid moves (buy box, feature icons); fading starts below it.
  const firstGrid = sections.findIndex((s) => EXCLUDE_ID.test(s.id));
  for (const section of sections.slice(firstGrid + 1)) {
    if (!(section instanceof HTMLElement)) continue;
    if (EXCLUDE_ID.test(section.id)) continue;
    if (section.querySelector(EXCLUDE_INSIDE)) continue;
    // No height check: app sections (Judge.me reviews) are often still empty
    // at this point and fill in later; an empty one hidden is invisible anyway.
    if (!belowFold(section)) continue; // already (partly) visible - leave static
    candidates.push([section, undefined]);
  }
  for (const part of document.querySelectorAll(BUNDLE_PARTS)) {
    if (part instanceof HTMLElement && belowFold(part)) candidates.push([part, undefined]);
  }
  for (const section of document.querySelectorAll('.shopify-section-group-footer-group')) {
    if (section instanceof HTMLElement && belowFold(section)) candidates.push([section, FOOTER_OPTIONS]);
  }
  if (!candidates.length) return;

  // CSS lives here, not in a stylesheet, so it only ever exists together with
  // the code that adds and removes the classes (rule 1). Stagger matches the
  // product cards, so bundle tiles sharing a row on desktop cascade the same way.
  // Doubled class: element CSS further down the page (e.g. .bundle-card's own
  // hover `transition`) otherwise wins on source order and the fade is skipped.
  const style = document.createElement('style');
  style.textContent = `
    .${REVEAL_CLASS}.${REVEAL_CLASS} {
      opacity: 0.01;
      transform: translateY(2rem);
      transition: opacity 0.6s cubic-bezier(0, 0, 0.3, 1), transform 0.6s cubic-bezier(0, 0, 0.3, 1);
      transition-delay: calc(var(--reveal-order, 0) * 120ms);
    }
    /* Footer: fade only. It is the last thing on the page, so a 2rem offset
       makes the page 2rem taller while hidden; when it rose back, the page
       shrank and the browser dragged everything above up with it (owner:
       motion sickness at the bottom of catalog pages). */
    .shopify-section-group-footer-group.${REVEAL_CLASS}.${REVEAL_CLASS} {
      transform: none;
    }
    .${REVEAL_CLASS}.${REVEAL_CLASS}.is-revealed {
      opacity: 1;
      transform: translateY(0);
    }
  `;
  document.head.appendChild(style);

  for (const [el, options] of candidates) arm(el, options);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
